import { useState, useEffect, useRef, useCallback } from "react";
import {
  Link2,
  QrCode,
  Copy,
  Shield,
  Plus,
  Download,
  FileText,
  ExternalLink,
  MoreHorizontal,
  XCircle,
  Search,
  ChevronLeft,
  ChevronRight,
  LayoutGrid,
  List,
} from "lucide-react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";
import { FeatureUnavailable } from "@/components/common/FeatureGate";
import { Badge } from "@/components/ui/badge";
import { Button, FloatingActionButton } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useIsMobile } from "@/hooks/useMobileUtils";
import { signingLinksApi, envelopeApi } from "@/services/api";
import { useAuthStore } from "@/store/auth.store";
import { formatDateShort } from "@/utils/dateFormat";

// Status badge component with consistent styling
const StatusBadge = ({ isActive }) =>
  isActive ? (
    <Badge className="bg-emerald-500 text-white border-0 font-medium">
      Active
    </Badge>
  ) : (
    <Badge className="bg-slate-500 text-white border-0 font-medium">
      Inactive
    </Badge>
  );

// Loading skeleton
const SigningLinksSkeleton = () => (
  <div className="space-y-6">
    <div className="flex items-center justify-between">
      <div className="space-y-2">
        <Skeleton className="h-8 w-32" />
        <Skeleton className="h-4 w-48" />
      </div>
      <Skeleton className="h-10 w-36" />
    </div>

    <div className="flex gap-4">
      <Skeleton className="h-10 w-64" />
      <Skeleton className="h-10 flex-1" />
      <Skeleton className="h-10 w-20" />
    </div>

    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="rounded-lg border bg-card overflow-hidden">
          <Skeleton className="h-32 rounded-none" />
          <div className="p-4 space-y-2">
            <Skeleton className="h-5 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
          </div>
        </div>
      ))}
    </div>
  </div>
);

// Empty state
const EmptyState = ({ isListMode, hasFilters, onCreateNew }) => {
  const { t } = useTranslation();

  const renderEmptyStateContent = () => {
    if (hasFilters) {
      return t("signingLinks.noLinksMatchFilters");
    }
    return isListMode
      ? t("signingLinks.createLinksDescription")
      : t("signingLinks.createLinksForDocument");
  };

  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted mb-4">
        <Link2 className="h-8 w-8 text-muted-foreground" />
      </div>
      <h3 className="text-lg font-semibold mb-1">
        {t("signingLinks.noSigningLinks")}
      </h3>
      <p className="text-sm text-muted-foreground mb-4 max-w-sm">
        {renderEmptyStateContent()}
      </p>
      {!hasFilters && (
        <Button onClick={onCreateNew}>
          <Plus className="mr-2 h-4 w-4" />
          Create Link
        </Button>
      )}
    </div>
  );
};

// Link list item (table row)
const LinkListItem = ({ link, isListMode, onCopy, onViewQR, onDeactivate }) => {
  const { t } = useTranslation();

  const user = useAuthStore((state) => state.user);

  return (
    <TableRow>
      {isListMode && (
        <TableCell>
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10">
              <FileText className="h-4 w-4 text-primary" />
            </div>
            <span className="font-medium truncate max-w-50">
              {link.envelopeTitle || t("common.unknown")}
            </span>
          </div>
        </TableCell>
      )}
      <TableCell>
        <div>
          <p className="font-medium">{link.recipientName || "Unknown"}</p>
          <p className="text-sm text-muted-foreground">{link.recipientEmail}</p>
        </div>
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-2">
          {link.linkType === "qr" ? (
            <QrCode className="h-4 w-4 text-muted-foreground" />
          ) : (
            <Link2 className="h-4 w-4 text-muted-foreground" />
          )}
          <span className="capitalize">{link.linkType || "direct"}</span>
          {link.pinProtected && <Shield className="h-4 w-4 text-amber-500" />}
        </div>
      </TableCell>
      <TableCell>
        <StatusBadge isActive={link.isActive} />
      </TableCell>
      <TableCell className="text-muted-foreground">
        {link.useCount || 0} / {link.maxUses || "∞"}
      </TableCell>
      <TableCell className="text-muted-foreground whitespace-nowrap">
        {link.expiresAt ? formatDateShort(link.expiresAt, user) : "Never"}
      </TableCell>
      <TableCell>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <MoreHorizontal className="h-4 w-4" />
              <span className="sr-only">{t("common.openMenu")}</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onCopy(link)}>
              <Copy className="mr-2 h-4 w-4" />
              Copy link
            </DropdownMenuItem>
            {link.url && (
              <DropdownMenuItem onClick={() => window.open(link.url, "_blank")}>
                <ExternalLink className="mr-2 h-4 w-4" />
                Open link
              </DropdownMenuItem>
            )}
            {link.linkToken && (
              <DropdownMenuItem onClick={() => onViewQR(link.linkToken)}>
                <QrCode className="mr-2 h-4 w-4" />
                View QR code
              </DropdownMenuItem>
            )}
            {link.isActive && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => onDeactivate(link.id)}
                  className="text-destructive focus:text-destructive"
                >
                  <XCircle className="mr-2 h-4 w-4" />
                  Deactivate
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </TableCell>
    </TableRow>
  );
};

// Grid card component for signing links
const LinkCard = ({ link, isListMode, onCopy, onViewQR, onDeactivate }) => {
  const user = useAuthStore((state) => state.user);

  return (
    <Card className="group overflow-hidden hover:shadow-md transition-shadow">
      {/* Preview area */}
      <div
        className={`h-32 flex items-center justify-center relative ${
          link.isActive ? "bg-emerald-50 dark:bg-emerald-900/20" : "bg-muted"
        }`}
      >
        <div
          className={`flex h-16 w-16 items-center justify-center rounded-xl ${
            link.isActive
              ? "bg-emerald-100 dark:bg-emerald-900/40"
              : "bg-muted-foreground/10"
          }`}
        >
          {link.linkType === "qr" ? (
            <QrCode
              className={`h-8 w-8 ${
                link.isActive
                  ? "text-emerald-600 dark:text-emerald-400"
                  : "text-muted-foreground"
              }`}
            />
          ) : (
            <Link2
              className={`h-8 w-8 ${
                link.isActive
                  ? "text-emerald-600 dark:text-emerald-400"
                  : "text-muted-foreground"
              }`}
            />
          )}
        </div>

        {/* Status badge */}
        <div className="absolute bottom-2 left-2">
          <StatusBadge isActive={link.isActive} />
        </div>

        {/* Quick actions */}
        <div className="absolute top-2 right-2 z-10">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="secondary" size="icon" className="h-8 w-8">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onCopy(link)}>
                <Copy className="mr-2 h-4 w-4" />
                Copy link
              </DropdownMenuItem>
              {link.url && (
                <DropdownMenuItem
                  onClick={() => window.open(link.url, "_blank")}
                >
                  <ExternalLink className="mr-2 h-4 w-4" />
                  Open link
                </DropdownMenuItem>
              )}
              {link.linkToken && (
                <DropdownMenuItem onClick={() => onViewQR(link.linkToken)}>
                  <QrCode className="mr-2 h-4 w-4" />
                  View QR code
                </DropdownMenuItem>
              )}
              {link.isActive && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => onDeactivate(link.id)}
                    className="text-destructive focus:text-destructive"
                  >
                    <XCircle className="mr-2 h-4 w-4" />
                    Deactivate
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Content */}
      <CardContent className="p-4 dark:bg-background">
        {isListMode && link.envelopeTitle && (
          <p className="text-xs text-muted-foreground truncate mb-1">
            {link.envelopeTitle}
          </p>
        )}
        <h3 className="font-medium truncate">
          {link.recipientName || "Unknown"}
        </h3>
        <p className="text-sm text-muted-foreground truncate mb-3">
          {link.recipientEmail}
        </p>

        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <div className="flex items-center gap-1">
            {link.pinProtected && <Shield className="h-3 w-3 text-amber-500" />}
            <span>
              {link.useCount || 0} / {link.maxUses || "∞"} uses
            </span>
          </div>
          <span>
            {link.expiresAt
              ? formatDateShort(link.expiresAt, user)
              : "No expiry"}
          </span>
        </div>
      </CardContent>
    </Card>
  );
};

// Create link dialog
const CreateLinkDialog = ({ open, onOpenChange, envelope, onCreated }) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [recipientId, setRecipientId] = useState("");
  const [linkType, setLinkType] = useState("direct");
  const [pin, setPin] = useState("");
  const [expiresIn, setExpiresIn] = useState("7d");
  const [maxUses, setMaxUses] = useState("");
  const [loading, setLoading] = useState(false);
  const [createForAll, setCreateForAll] = useState(false);

  const handleCreate = async () => {
    setLoading(true);
    try {
      if (createForAll) {
        await signingLinksApi.createForAll(envelope.id, {
          linkType,
          pin: pin || undefined,
          expiresIn,
          maxUses: maxUses ? parseInt(maxUses) : undefined,
        });
        toast.success(t("toast.linksCreatedForAll"));
      } else {
        await signingLinksApi.create(envelope.id, recipientId, {
          linkType,
          pin: pin || undefined,
          expiresIn,
          maxUses: maxUses ? parseInt(maxUses) : undefined,
        });
        toast.success(t("toast.signingLinkCreated"));
      }
      onOpenChange(false);
      onCreated();
      // Reset form
      setRecipientId("");
      setLinkType("direct");
      setPin("");
      setExpiresIn("7d");
      setMaxUses("");
      setCreateForAll(false);
    } catch (error) {
      toast.error(t("toast.failedToCreateLink"));
      console.error("Failed to create link:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("signingLinks.createLink")}</DialogTitle>
          <DialogDescription>
            {t("signingLinks.createLinkDesc")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="flex items-center gap-2">
            <Checkbox
              id="createForAll"
              checked={createForAll}
              onCheckedChange={setCreateForAll}
            />
            <Label
              htmlFor="createForAll"
              className="text-sm font-normal cursor-pointer"
            >
              {t("signingLinks.createForAll")}
            </Label>
          </div>

          {!createForAll && (
            <div className="space-y-2">
              <Label>{t("signingLinks.recipient")}</Label>
              <Select value={recipientId} onValueChange={setRecipientId}>
                <SelectTrigger>
                  <SelectValue
                    placeholder={t("signingLinks.selectRecipient")}
                  />
                </SelectTrigger>
                <SelectContent>
                  {envelope?.recipients?.map((r) => (
                    <SelectItem key={r.id} value={r.id}>
                      {r.name || r.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-2">
            <Label>{t("signingLinks.linkType")}</Label>
            <Select value={linkType} onValueChange={setLinkType}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="direct">
                  {t("signingLinks.types.direct")}
                </SelectItem>
                <SelectItem value="qr">{t("signingLinks.types.qr")}</SelectItem>
                <SelectItem value="embed">
                  {t("signingLinks.types.embed")}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>{t("signingLinks.pin")}</Label>
            <Input
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              placeholder={t("signingLinks.pinPlaceholder")}
              maxLength={6}
            />
            <p className="text-sm text-muted-foreground">
              {t("signingLinks.pinHint")}
            </p>
          </div>

          <div className="space-y-2">
            <Label>{t("signingLinks.expiresIn")}</Label>
            <Select value={expiresIn} onValueChange={setExpiresIn}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1d">
                  {t("signingLinks.expiry.1d")}
                </SelectItem>
                <SelectItem value="7d">
                  {t("signingLinks.expiry.7d")}
                </SelectItem>
                <SelectItem value="30d">
                  {t("signingLinks.expiry.30d")}
                </SelectItem>
                <SelectItem value="never">
                  {t("signingLinks.expiry.never")}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>{t("signingLinks.maxUses")}</Label>
            <Input
              type="number"
              value={maxUses}
              onChange={(e) => setMaxUses(e.target.value)}
              placeholder={t("signingLinks.unlimited")}
              min="1"
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            size={isMobile ? "mobile" : "default"}
          >
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleCreate}
            disabled={loading || (!createForAll && !recipientId)}
            size={isMobile ? "mobile" : "default"}
          >
            {loading ? t("common.creating") : t("signingLinks.createLink")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Envelope selector dialog for creating links from list view
const EnvelopeSelectDialog = ({ open, onOpenChange, onSelect }) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [envelopes, setEnvelopes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selecting, setSelecting] = useState(null);
  const [search, setSearch] = useState("");

  // Track the current request to handle race conditions
  const requestIdRef = useRef(0);
  const debounceTimerRef = useRef(null);

  // Fetch envelopes function
  const fetchEnvelopes = useCallback(async (searchTerm) => {
    // Increment request ID to track this specific request
    const currentRequestId = ++requestIdRef.current;

    setLoading(true);

    try {
      const res = await envelopeApi.list({
        limit: 50,
        search: searchTerm || undefined,
      });

      // Only update state if this is still the latest request
      // (prevents race conditions where older requests complete after newer ones)
      if (currentRequestId !== requestIdRef.current) {
        return;
      }

      // Parse response - the API returns { success, data: [...], pagination }
      // res.data is the axios response body, res.data.data is the envelopes array
      const responseData = res.data?.data;
      const envelopesArray = Array.isArray(responseData) ? responseData : [];

      // Filter to only show relevant statuses
      const validStatuses = ["draft", "sent", "viewed", "partially_signed"];
      const filtered = envelopesArray.filter((e) =>
        validStatuses.includes(e.status),
      );

      setEnvelopes(filtered);
    } catch (err) {
      console.error("Failed to fetch envelopes:", err);
      // Only set empty on error if this is still the latest request
      if (currentRequestId === requestIdRef.current) {
        setEnvelopes([]);
      }
    } finally {
      // Only clear loading if this is still the latest request
      if (currentRequestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, []);

  // Handle dialog open/close
  useEffect(() => {
    if (open) {
      // Reset state when dialog opens
      setSearch("");
      setEnvelopes([]);
      requestIdRef.current = 0;

      // Clear any pending debounce
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }

      // Fetch initial data immediately
      fetchEnvelopes("");
    } else {
      // Clean up when dialog closes
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    }
  }, [open, fetchEnvelopes]);

  // Handle search input changes with debouncing
  const handleSearchChange = useCallback(
    (e) => {
      const newValue = e.target.value;
      setSearch(newValue);

      // Clear existing debounce timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Set new debounce timer
      debounceTimerRef.current = setTimeout(() => {
        fetchEnvelopes(newValue);
      }, 400);
    },
    [fetchEnvelopes],
  );

  // Cleanup on unmount
  useEffect(
    () => () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    },
    [],
  );

  const handleSelect = async (envelope) => {
    setSelecting(envelope.id);
    try {
      // Fetch full envelope data including recipients
      const res = await envelopeApi.getById(envelope.id);
      const fullEnvelope = res.data?.data || res.data;
      onSelect(fullEnvelope);
      onOpenChange(false);
    } catch (error) {
      console.error("Failed to load envelope:", error);
      toast.error(t("toast.failedToLoadEnvelope"));
    } finally {
      setSelecting(null);
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "draft":
        return t("status.draft");
      case "sent":
        return t("status.sent");
      case "viewed":
        return t("status.viewed");
      case "partially_signed":
        return t("status.partiallySigned");
      default:
        return status;
    }
  };

  const renderSigningLinksContent = () =>
    envelopes.length === 0 ? (
      <p className="text-sm text-muted-foreground text-center py-4">
        {search
          ? t("signingLinks.noEnvelopesMatch")
          : t("signingLinks.noEnvelopesFound")}
      </p>
    ) : (
      envelopes.map((envelope) => (
        <button
          key={envelope.id}
          onClick={() => handleSelect(envelope)}
          disabled={selecting === envelope.id}
          className="w-full text-left p-3 rounded-md hover:bg-muted transition-colors flex items-center gap-3 disabled:opacity-50"
        >
          <FileText className="h-5 w-5 text-muted-foreground shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="font-medium truncate">{envelope.title}</p>
            <p className="text-sm text-muted-foreground">
              {selecting === envelope.id
                ? t("common.loading")
                : `${getStatusLabel(envelope.status)} · ${
                    envelope.recipients?.length || 0
                  } recipient(s)`}
            </p>
          </div>
          <Badge variant="outline" className="shrink-0">
            {getStatusLabel(envelope.status)}
          </Badge>
        </button>
      ))
    );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("signingLinks.selectEnvelope")}</DialogTitle>
          <DialogDescription>
            {t("signingLinks.selectEnvelopeDesc")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="relative">
            <Input
              placeholder={t("envelopes.searchPlaceholder")}
              value={search}
              onChange={handleSearchChange}
            />
            {loading && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent" />
              </div>
            )}
          </div>

          <div className="max-h-64 overflow-y-auto space-y-1">
            {loading && envelopes.length === 0 ? (
              <div className="space-y-2">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : (
              renderSigningLinksContent()
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            size={isMobile ? "mobile" : "default"}
          >
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// QR Code dialog
const QRCodeDialog = ({ open, onOpenChange, token }) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [qrCode, setQrCode] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (open && token) {
      setLoading(true);
      signingLinksApi
        .getQRCode(token)
        .then((res) => {
          // API returns { url, dataUrl, svg } - dataUrl is the base64 image
          setQrCode(res.data?.data?.dataUrl || res.data?.dataUrl);
        })
        .catch(console.error)
        .finally(() => setLoading(false));
    }
  }, [open, token]);

  const handleDownload = () => {
    if (qrCode) {
      const a = document.createElement("a");
      a.href = qrCode;
      a.download = `signing-qr-${token.slice(0, 8)}.png`;
      a.click();
      toast.success(t("toast.qrCodeDownloaded"));
    }
  };

  const renderQrCodeContent = () =>
    qrCode ? (
      <img src={qrCode} alt="QR Code" className="w-48 h-48" />
    ) : (
      <div className="w-48 h-48 flex items-center justify-center bg-muted rounded-lg">
        <p className="text-sm text-muted-foreground">
          {t("errors.failedToLoad")}
        </p>
      </div>
    );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>{t("common.qrCode")}</DialogTitle>
          <DialogDescription>
            Scan this code to access the signing page
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col items-center py-4">
          {loading ? <Skeleton className="w-48 h-48" /> : renderQrCodeContent()}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            size={isMobile ? "mobile" : "default"}
          >
            Close
          </Button>
          <Button
            onClick={handleDownload}
            disabled={!qrCode}
            size={isMobile ? "mobile" : "default"}
          >
            <Download className="mr-2 h-4 w-4" />
            Download
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Pagination component
const Pagination = ({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
}) => {
  const from = (currentPage - 1) * itemsPerPage + 1;
  const to = Math.min(currentPage * itemsPerPage, totalItems);

  if (totalPages <= 1) return null;

  return (
    <div className="flex items-center justify-between px-4 py-3 border-t">
      <p className="text-sm text-muted-foreground">
        Showing {from} to {to} of {totalItems} results
      </p>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          disabled={currentPage === 1}
          onClick={() => onPageChange(currentPage - 1)}
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          Previous
        </Button>
        <div className="flex items-center gap-1">
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            let pageNum;
            if (totalPages <= 5) {
              pageNum = i + 1;
            } else if (currentPage <= 3) {
              pageNum = i + 1;
            } else if (currentPage >= totalPages - 2) {
              pageNum = totalPages - 4 + i;
            } else {
              pageNum = currentPage - 2 + i;
            }

            return (
              <Button
                key={pageNum}
                variant={currentPage === pageNum ? "default" : "outline"}
                size="sm"
                className="w-8 h-8 p-0"
                onClick={() => onPageChange(pageNum)}
              >
                {pageNum}
              </Button>
            );
          })}
        </div>
        <Button
          variant="outline"
          size="sm"
          disabled={currentPage >= totalPages}
          onClick={() => onPageChange(currentPage + 1)}
        >
          Next
          <ChevronRight className="h-4 w-4 ml-1" />
        </Button>
      </div>
    </div>
  );
};

// Main SigningLinks component
export const SigningLinks = () => {
  const { t } = useTranslation();
  const { envelopeId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const isMobile = useIsMobile();

  const [envelope, setEnvelope] = useState(null);
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [featureError, setFeatureError] = useState(null);
  const [viewMode, setViewMode] = useState("grid"); // 'grid' or 'list'
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
  });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showQRModal, setShowQRModal] = useState(null);
  const [showEnvelopeSelector, setShowEnvelopeSelector] = useState(false);
  const [selectedEnvelope, setSelectedEnvelope] = useState(null);
  const [search, setSearch] = useState(searchParams.get("search") || "");

  const isListMode = !envelopeId;
  const currentStatus = searchParams.get("status") || "all";
  const currentPage = parseInt(searchParams.get("page") || "1", 10);

  const loadData = useCallback(async () => {
    setLoading(true);
    setFeatureError(null);
    try {
      if (isListMode) {
        const linksRes = await signingLinksApi.list({
          status: currentStatus === "all" ? undefined : currentStatus,
          limit: 10,
          offset: (currentPage - 1) * 10,
        });
        const data = linksRes.data?.data;
        const linksList = data?.links || data || [];
        setLinks(linksList);
        setPagination({
          page: currentPage,
          limit: 10,
          total: data?.total || linksList.length || 0,
        });
      } else {
        const [envelopeRes, linksRes] = await Promise.all([
          envelopeApi.getById(envelopeId),
          signingLinksApi.getByEnvelope(envelopeId),
        ]);
        setEnvelope(envelopeRes.data?.data);
        const linksData = linksRes.data?.data;
        const allLinks = Array.isArray(linksData)
          ? linksData
          : linksData?.links || [];
        // Filter by status if needed
        const filteredLinks =
          currentStatus === "all"
            ? allLinks
            : allLinks.filter((l) =>
                currentStatus === "active" ? l.isActive : !l.isActive,
              );
        setLinks(filteredLinks);
        setPagination({
          page: 1,
          limit: filteredLinks.length,
          total: filteredLinks.length,
        });
      }
    } catch (error) {
      console.error("Failed to load data:", error);
      const errorData = error.response?.data;

      // Check for feature-gated error
      if (
        error.response?.status === 403 &&
        (errorData?.code === "FEATURE_UNAVAILABLE" ||
          errorData?.code === "FEATURE_NOT_AVAILABLE" ||
          errorData?.error?.code === "FEATURE_NOT_AVAILABLE" ||
          errorData?.details?.reason === "plan_required")
      ) {
        setFeatureError({
          message:
            errorData?.error?.message ||
            errorData?.error ||
            t("errors.featureUnavailable"),
          requiredPlan:
            errorData?.error?.featureInfo?.minimumPlan ||
            errorData?.details?.requiredPlan ||
            "starter",
          upgradeUrl: errorData?.upgradeUrl || "/settings/billing",
        });
      } else {
        toast.error(t("toast.failedToLoadSigningLinks"));
      }
    } finally {
      setLoading(false);
    }
  }, [currentPage, currentStatus, envelopeId, isListMode, t]);

  useEffect(() => {
    loadData();
  }, [envelopeId, currentStatus, currentPage, loadData]);

  const handleStatusChange = (status) => {
    const params = new URLSearchParams(searchParams);
    params.set("status", status);
    params.set("page");
    setSearchParams(params);
  };

  const handlePageChange = (newPage) => {
    const params = new URLSearchParams(searchParams);
    params.set("page", newPage.toString());
    setSearchParams(params);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams(searchParams);
    if (search) {
      params.set("search", search);
    } else {
      params.delete("search");
    }
    params.set("page");
    setSearchParams(params);
  };

  const copyLink = (link) => {
    navigator.clipboard.writeText(link.url);
    toast.success(t("toast.linkCopied"));
  };

  const deactivateLink = async (linkId) => {
    if (!confirm(t("signingLinks.confirmDeactivate"))) return;

    try {
      await signingLinksApi.deactivate(linkId);
      toast.success(t("toast.linkDeactivated"));
      loadData();
    } catch (error) {
      toast.error(t("toast.failedToDeactivateLink"));
      console.error("Failed to deactivate link:", error);
    }
  };

  const totalPages = Math.ceil(pagination.total / pagination.limit);
  const hasFilters = currentStatus !== "all" || search;

  const renderLinksContent = () =>
    viewMode === "grid" ? (
      <>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {links.map((link) => (
            <LinkCard
              key={link.id}
              link={link}
              isListMode={isListMode}
              onCopy={copyLink}
              onViewQR={setShowQRModal}
              onDeactivate={deactivateLink}
            />
          ))}
        </div>

        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={pagination.total}
          itemsPerPage={pagination.limit}
          onPageChange={handlePageChange}
        />
      </>
    ) : (
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                {isListMode && <TableHead>{t("envelopes.title")}</TableHead>}
                <TableHead>{t("signingLinks.recipient")}</TableHead>
                <TableHead>{t("common.type")}</TableHead>
                <TableHead>{t("common.status")}</TableHead>
                <TableHead>{t("signingLinks.uses")}</TableHead>
                <TableHead>{t("signingLinks.expires")}</TableHead>
                <TableHead className="w-12.5" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {links.map((link) => (
                <LinkListItem
                  key={link.id}
                  link={link}
                  isListMode={isListMode}
                  onCopy={copyLink}
                  onViewQR={setShowQRModal}
                  onDeactivate={deactivateLink}
                />
              ))}
            </TableBody>
          </Table>

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={pagination.total}
            itemsPerPage={pagination.limit}
            onPageChange={handlePageChange}
          />
        </CardContent>
      </Card>
    );

  // Show upgrade prompt if feature is not available
  if (featureError) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold font-heading tracking-tight">
            Signing Links
          </h1>
          <p className="text-muted-foreground">
            Create shareable links for document signing
          </p>
        </div>

        <FeatureUnavailable error={featureError} icon={Link2} />

        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">
              Signing links allow recipients to access and sign documents via
              direct URLs or QR codes, without needing email invitations.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading && links.length === 0) {
    return <SigningLinksSkeleton />;
  }

  return (
    <div className="space-y-6 pb-20 sm:pb-0">
      {/* Page header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            Signing Links
          </h1>
          <p className="text-muted-foreground">
            {isListMode
              ? t("signingLinks.manageAllLinks")
              : envelope?.title || t("signingLinks.manageLinksForEnvelope")}
          </p>
        </div>
        <Button
          onClick={() => {
            if (isListMode) {
              setShowEnvelopeSelector(true);
            } else {
              setShowCreateModal(true);
            }
          }}
          className="hidden sm:inline-flex"
        >
          <Plus className="mr-2 h-4 w-4" />
          Create Link
        </Button>
      </div>

      {/* Mobile FAB */}
      {isMobile && (
        <FloatingActionButton
          icon={Plus}
          onClick={() => {
            if (isListMode) {
              setShowEnvelopeSelector(true);
            } else {
              setShowCreateModal(true);
            }
          }}
          label="Create Link"
        />
      )}

      {/* Filters */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
          <Tabs
            value={currentStatus}
            onValueChange={handleStatusChange}
            className="w-full sm:w-auto"
          >
            <TabsList>
              <TabsTrigger value="all">{t("common.all")}</TabsTrigger>
              <TabsTrigger value="active">{t("common.active")}</TabsTrigger>
              <TabsTrigger value="inactive">
                {t("signingLinks.inactive")}
              </TabsTrigger>
            </TabsList>
          </Tabs>

          <form onSubmit={handleSearch} className="flex flex-1 gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={t("signingLinks.searchPlaceholder")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <Button type="submit" variant="secondary">
              {t("common.search")}
            </Button>
          </form>
        </div>

        {/* View toggle */}
        <div className="flex items-center border rounded-lg p-1">
          <Button
            variant={viewMode === "grid" ? "secondary" : "ghost"}
            size="sm"
            className="h-8 px-2"
            onClick={() => setViewMode("grid")}
          >
            <LayoutGrid className="h-4 w-4" />
          </Button>
          <Button
            variant={viewMode === "list" ? "secondary" : "ghost"}
            size="sm"
            className="h-8 px-2"
            onClick={() => setViewMode("list")}
          >
            <List className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Links grid/list */}
      {links.length === 0 ? (
        <Card>
          <CardContent className="p-0">
            <EmptyState
              isListMode={isListMode}
              hasFilters={hasFilters}
              onCreateNew={() => {
                if (isListMode) {
                  setShowEnvelopeSelector(true);
                } else {
                  setShowCreateModal(true);
                }
              }}
            />
          </CardContent>
        </Card>
      ) : (
        renderLinksContent()
      )}

      {/* Create link dialog */}
      {(envelope || selectedEnvelope) && (
        <CreateLinkDialog
          open={showCreateModal}
          onOpenChange={(open) => {
            setShowCreateModal(open);
            if (!open && isListMode) {
              setSelectedEnvelope(null);
            }
          }}
          envelope={envelope || selectedEnvelope}
          onCreated={loadData}
        />
      )}

      {/* Envelope selector dialog (for list mode) */}
      <EnvelopeSelectDialog
        open={showEnvelopeSelector}
        onOpenChange={setShowEnvelopeSelector}
        onSelect={(env) => {
          setSelectedEnvelope(env);
          setShowCreateModal(true);
        }}
      />

      {/* QR Code dialog */}
      <QRCodeDialog
        open={!!showQRModal}
        onOpenChange={(open) => !open && setShowQRModal(null)}
        token={showQRModal}
      />
    </div>
  );
};

export default SigningLinks;
